import Phaser from 'phaser';
import { openInventory } from '../crafting/mode';
import { getInventoryFilter, unequipSlot, type EquipFilter } from '../equipment';
import { equipmentSlotPosition } from '../hud-viewport';
import { playerState } from '../player-state';
import { ensureItemTexture, fitItemIcon } from './item-textures';

const SLOT_TYPES = ['head', 'body', 'weapon'] as const;
const PLACEHOLDER_KEYS: Record<(typeof SLOT_TYPES)[number], string> = {
  head: 'ui-ph-head',
  body: 'ui-ph-body',
  weapon: 'ui-ph-weapon',
};

function equippedItemName(type: (typeof SLOT_TYPES)[number]) {
  if (type === 'head') return playerState.equipped.head;
  if (type === 'body') return playerState.equipped.body;
  return playerState.equipped.weapon;
}

export function createEquipmentPanel(scene: Phaser.Scene) {
  const root = scene.add.container(0, 0).setScrollFactor(0).setDepth(101).setVisible(false);

  const slots: Record<
    string,
    { root: Phaser.GameObjects.Container; item: Phaser.GameObjects.Image; ph: Phaser.GameObjects.Image; size: number }
  > = {};

  SLOT_TYPES.forEach((type, index) => {
    const { x, y, size } = equipmentSlotPosition(index);
    const slotRoot = scene.add.container(x, y);
    const bg = scene.add.image(0, 0, 'ui-slot-square').setOrigin(0, 0).setDisplaySize(size, size);
    const ph = scene.add
      .image(size / 2, size / 2, PLACEHOLDER_KEYS[type])
      .setDisplaySize(size * 0.6, size * 0.6);
    const item = scene.add.image(size / 2, size / 2, PLACEHOLDER_KEYS[type]).setVisible(false);
    fitItemIcon(item, size, 0.7);

    const hit = scene.add
      .rectangle(size / 2, size / 2, size, size, 0x000000, 0.001)
      .setInteractive({ useHandCursor: true });

    hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      if (type !== 'weapon' && equippedItemName(type)) {
        unequipSlot(type);
        refresh();
        return;
      }
      openInventory(type as EquipFilter);
    });

    slotRoot.add([bg, ph, item, hit]);
    root.add(slotRoot);
    slots[type] = { root: slotRoot, item, ph, size };
  });

  async function refresh() {
    const filter = getInventoryFilter();
    for (const type of SLOT_TYPES) {
      const slot = slots[type];
      if (!slot) continue;

      const active = Boolean(filter && filter === type);
      slot.root.setScale(active ? 1.04 : 1);

      const itemName = equippedItemName(type);
      if (itemName) {
        const tex = await ensureItemTexture(scene, itemName);
        if (tex && slot.item.active) {
          slot.item.setTexture(tex).setVisible(true);
          fitItemIcon(slot.item, slot.size, 0.7);
          slot.ph.setVisible(false);
          continue;
        }
      }
      slot.item.setVisible(false);
      slot.ph.setVisible(true);
    }
  }

  function relayout() {
    for (let index = 0; index < SLOT_TYPES.length; index++) {
      const type = SLOT_TYPES[index];
      const slot = slots[type];
      if (!slot) continue;
      const { x, y, size } = equipmentSlotPosition(index);
      slot.root.setPosition(x, y);
      slot.size = size;
      slot.root.list.forEach((child) => {
        if (child instanceof Phaser.GameObjects.Image) {
          const img = child;
          if (img.texture.key === 'ui-slot-square') {
            img.setDisplaySize(size, size).setPosition(0, 0);
          } else if (img === slot.ph) {
            img.setPosition(size / 2, size / 2).setDisplaySize(size * 0.6, size * 0.6);
          } else if (img === slot.item) {
            img.setPosition(size / 2, size / 2);
            fitItemIcon(img, size, 0.7);
          }
        } else if (child instanceof Phaser.GameObjects.Rectangle) {
          child.setPosition(size / 2, size / 2).setSize(size, size);
        }
      });
    }
    void refresh();
  }

  return {
    root,
    refresh,
    relayout,
    setVisible(visible: boolean) {
      root.setVisible(visible);
    },
  };
}
